import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Map as MapIcon, ListOrdered, Route, Crosshair } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";

const blueIcon = (n) => new L.DivIcon({
  className: "",
  html: `<div style="background:#0055FF;width:30px;height:30px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.25);color:white;display:flex;align-items:center;justify-content:center;font-weight:800;font-family:Outfit,sans-serif;font-size:13px;">${n}</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const greenIcon = new L.DivIcon({
  className: "",
  html: `<div style="background:#34C759;width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.25);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const startIcon = new L.DivIcon({
  className: "",
  html: `<div style="background:#FF9500;width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 12px rgba(0,0,0,0.3);color:white;display:flex;align-items:center;justify-content:center;font-weight:800;">★</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

// Greedy nearest-neighbour TSP from a start point
const optimizeRoute = (start, points) => {
  if (!points.length) return [];
  const remaining = [...points];
  const ordered = [];
  let cur = start;
  while (remaining.length) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const p = remaining[i];
      const dx = p.lat - cur.lat;
      const dy = p.lng - cur.lng;
      const d = dx * dx + dy * dy;
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    ordered.push(next);
    cur = next;
  }
  return ordered;
};

export default function SalesmanMap() {
  const nav = useNavigate();
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optimized, setOptimized] = useState(false);
  const [myLocation, setMyLocation] = useState(null);

  useEffect(() => {
    api.get("/orders").then(r => setOrders(r.data)).finally(() => setLoading(false));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { timeout: 8000, enableHighAccuracy: false }
      );
    }
  }, []);

  const delivered = orders.filter(o => o.delivery_geo && o.delivery_geo.lat);
  const assigned = orders.filter(o => o.status === "assigned" || o.status === "out_for_delivery");

  const center = useMemo(() => {
    const pts = delivered.map(o => [o.delivery_geo.lat, o.delivery_geo.lng]);
    if (myLocation) pts.push([myLocation.lat, myLocation.lng]);
    if (!pts.length) return [13.0827, 80.2707];
    return [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
  }, [delivered, myLocation]);

  // For optimization: assigned shops with synthetic geo (centered offset) - in production each shop would have geo
  const assignedWithGeo = useMemo(() => {
    return assigned.map((o, i) => ({
      ...o,
      route_geo: o.shop?.geo || (o.delivery_geo ? { lat: o.delivery_geo.lat, lng: o.delivery_geo.lng }
        : { lat: center[0] + 0.005 * (i + 1), lng: center[1] + 0.005 * (i % 2 ? 1 : -1) }),
    }));
  }, [assigned, center]);

  const start = myLocation || { lat: center[0], lng: center[1] };
  const route = optimized ? optimizeRoute(start, assignedWithGeo.map(o => ({ ...o.route_geo, _order: o })))
    : assignedWithGeo.map(o => ({ ...o.route_geo, _order: o }));

  const polylinePoints = optimized && route.length > 0
    ? [[start.lat, start.lng], ...route.map(p => [p.lat, p.lng])]
    : [];

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <TopBar title={t("route_map")} onBack={() => nav("/salesman")} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4 fade-up">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-xl soft-shadow">
            <span className="w-3 h-3 rounded-full bg-[#34C759]" /> {t("delivered_today")} ({delivered.length})
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-xl soft-shadow">
            <span className="w-3 h-3 rounded-full bg-[#0055FF]" /> {t("assigned")} ({assignedWithGeo.length})
          </div>
          {myLocation && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-900 rounded-xl">
              <Crosshair className="w-3.5 h-3.5" /> Your location
            </div>
          )}
          <div className="flex-1" />
          <button
            onClick={() => setOptimized(o => !o)}
            data-testid="optimize-route-btn"
            disabled={assignedWithGeo.length < 2}
            className={`rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2 transition shadow-sm disabled:opacity-50 ${optimized ? "bg-[#0055FF] text-white" : "bg-white border border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white"}`}
          >
            <Route className="w-4 h-4" /> {optimized ? "Showing Best Route" : "Optimize Route"}
          </button>
        </div>

        {optimized && route.length > 0 && (
          <div className="bg-white rounded-2xl soft-shadow p-4" data-testid="route-list">
            <div className="text-xs uppercase tracking-wider text-[#86868B] font-bold mb-2">Suggested order ({route.length} stops)</div>
            <div className="flex flex-wrap gap-2">
              {route.map((p, i) => (
                <div key={i} className="flex items-center gap-2 bg-[#F5F5F7] rounded-lg px-2 py-1 text-xs">
                  <span className="w-5 h-5 rounded-full bg-[#0055FF] text-white grid place-items-center font-bold text-[10px]">{i + 1}</span>
                  <span className="font-semibold">{p._order.shop_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#86868B] py-12">{t("loading")}</div>
        ) : delivered.length === 0 && assignedWithGeo.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center soft-shadow">
            <MapIcon className="w-12 h-12 mx-auto text-[#86868B] mb-2" />
            <div className="text-[#86868B]">{t("no_locations")}</div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl overflow-hidden soft-shadow border border-gray-100" style={{ height: "60vh", minHeight: 400 }}>
            <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {myLocation && (
                <Marker position={[myLocation.lat, myLocation.lng]} icon={startIcon}>
                  <Popup>You are here</Popup>
                </Marker>
              )}
              {delivered.map(o => (
                <Marker key={o.id} position={[o.delivery_geo.lat, o.delivery_geo.lng]} icon={greenIcon}>
                  <Popup>
                    <div className="font-bold heading-font">{o.shop_name}</div>
                    <div className="text-xs text-[#86868B]">Delivered • {o.total_units || 0} {t("boxes")}</div>
                  </Popup>
                </Marker>
              ))}
              {route.map((p, i) => (
                <Marker key={p._order.id} position={[p.lat, p.lng]} icon={blueIcon(i + 1)}>
                  <Popup>
                    <div className="font-bold heading-font">#{i + 1} {p._order.shop_name}</div>
                    <div className="text-xs text-[#86868B]">{p._order.total_units || 0} {t("boxes")}</div>
                    {p._order.shop?.phone && <a href={`tel:${p._order.shop.phone}`} className="text-xs text-[#0055FF] font-semibold">📞 {p._order.shop.phone}</a>}
                  </Popup>
                </Marker>
              ))}
              {polylinePoints.length > 0 && (
                <Polyline positions={polylinePoints} pathOptions={{ color: "#0055FF", weight: 4, dashArray: "8 8", opacity: 0.7 }} />
              )}
            </MapContainer>
          </div>
        )}

        <button onClick={() => nav("/salesman")} data-testid="back-to-list" className="w-full bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-2xl py-3 font-bold flex items-center justify-center gap-2 transition">
          <ListOrdered className="w-4 h-4" /> Back to Orders List
        </button>
      </div>
    </div>
  );
}
